import React, { useContext, useEffect, useState } from "react";
import { Button, Form, FormGroup, Input, Label } from "reactstrap";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import UserContext from "./UserContext";
import ValetApi from "../api/Api";
import LiveCodeScannerModal from "../components/LiveCodeScannerModal";
import { parseTicketFromPayload } from "../utility/ticketScanDecoder";
import "./ValidationPortal.css";

function formatCents(cents, currency = "usd") {
    const value = Number(cents || 0) / 100;
    try {
        return new Intl.NumberFormat("en-US", {
            style: "currency",
            currency: String(currency || "usd").toUpperCase(),
        }).format(value);
    } catch {
        return `$${value.toFixed(2)}`;
    }
}

function parseValidationTokenFromPayload(rawText) {
    const raw = String(rawText || "").trim();
    if (!raw) return "";

    const direct = raw.match(/(VAL-[A-Z0-9-]{6,})/i);
    if (direct) return direct[1].toUpperCase();

    const pieces = raw
        .split(":")
        .map((piece) => String(piece || "").trim())
        .filter(Boolean);
    if (!pieces.length) return "";

    const lastPiece = pieces[pieces.length - 1].toUpperCase();
    return /^VAL-[A-Z0-9-]{6,}$/.test(lastPiece) ? lastPiece : "";
}

const ValidationRedeemPage = () => {
    const { userDetail } = useContext(UserContext);
    const location = useLocation();
    const roleId = Number(userDetail?.roleId || 0);
    const isTenantPortal = Boolean(userDetail?.isTenantPortal);

    const [tokenCode, setTokenCode] = useState("");
    const [ticketNum, setTicketNum] = useState("");
    const [mobile, setMobile] = useState("");
    const [note, setNote] = useState("");

    const [lookupStatus, setLookupStatus] = useState(null);
    const [lastRedemption, setLastRedemption] = useState(null);

    const [isLookingUp, setIsLookingUp] = useState(false);
    const [isRedeeming, setIsRedeeming] = useState(false);

    const [isTokenScannerOpen, setIsTokenScannerOpen] = useState(false);
    const [isTicketScannerOpen, setIsTicketScannerOpen] = useState(false);

    useEffect(() => {
        const stateTicket = String(location?.state?.ticketNum || "").replace(/\D/g, "");
        const stateMobile = String(location?.state?.mobile || "").trim();

        if (stateTicket) {
            setTicketNum(stateTicket);
        }

        if (stateMobile) {
            setMobile(stateMobile);
        }
    }, [location?.state?.ticketNum, location?.state?.mobile]);

    async function handleLookup(evt) {
        evt.preventDefault();

        const normalizedTicket = String(ticketNum || "").replace(/\D/g, "");
        const normalizedMobile = String(mobile || "").trim();

        if (!normalizedTicket && !normalizedMobile) {
            toast.error("Provide ticket number or mobile", { autoClose: 1200 });
            return;
        }

        try {
            setIsLookingUp(true);
            const res = isTenantPortal
                ? await ValetApi.lookupTenantValidationVisit({
                      ticketNum: normalizedTicket ? Number(normalizedTicket) : undefined,
                      mobile: normalizedMobile || undefined,
                  })
                : await ValetApi.lookupValidationVisit({
                      ticketNum: normalizedTicket ? Number(normalizedTicket) : undefined,
                      mobile: normalizedMobile || undefined,
                  });
            setLookupStatus(res?.data?.status || null);
            setLastRedemption(null);
        } catch (err) {
            setLookupStatus(null);
            toast.error(err?.[0] || err?.response?.data?.error?.message || "Unable to lookup visit", { autoClose: 1500 });
        } finally {
            setIsLookingUp(false);
        }
    }

    async function handleRedeem(evt) {
        evt.preventDefault();

        const normalizedTokenCode = String(tokenCode || "")
            .trim()
            .toUpperCase();
        const normalizedTicket = String(ticketNum || "").replace(/\D/g, "");
        const normalizedMobile = String(mobile || "").trim();

        if (!normalizedTokenCode) {
            toast.error("Validation token is required", { autoClose: 1200 });
            return;
        }

        if (!normalizedTicket && !normalizedMobile) {
            toast.error("Provide ticket number or mobile", { autoClose: 1200 });
            return;
        }

        try {
            setIsRedeeming(true);
            const payload = {
                tokenCode: normalizedTokenCode,
                ticketNum: normalizedTicket ? Number(normalizedTicket) : undefined,
                mobile: normalizedMobile || undefined,
                note: String(note || "").trim() || undefined,
            };

            const res = isTenantPortal ? await ValetApi.redeemTenantValidationToken(payload) : await ValetApi.redeemValidationToken(payload);

            const redemption = res?.data?.redemption || null;
            setLastRedemption(redemption);
            if (redemption?.statusAfter) {
                setLookupStatus(redemption.statusAfter);
            }
            setTokenCode("");
            toast.success("Validation redeemed successfully", { autoClose: 1400 });
        } catch (err) {
            toast.error(err?.[0] || err?.response?.data?.error?.message || "Unable to redeem validation token", { autoClose: 1600 });
        } finally {
            setIsRedeeming(false);
        }
    }

    if (!userDetail) {
        return (
            <section className="page-scroll">
                <div className="page-scroll-inner validation-page-shell">
                    <h2>Validation Redemption Portal</h2>
                    <p>Loading user permissions...</p>
                </div>
            </section>
        );
    }

    if (!isTenantPortal && roleId < 1) {
        return (
            <section className="page-scroll">
                <div className="page-scroll-inner validation-page-shell">
                    <h2>Validation Redemption Portal</h2>
                    <p>Login required.</p>
                </div>
            </section>
        );
    }

    return (
        <section className="page-scroll">
            <div className="page-scroll-inner validation-page-shell">
                <div className="validation-page-header">
                    <h2>Validation Redemption Portal</h2>
                    <p>Scan or enter a single-use validation token, then redeem against an active visit by ticket or mobile.</p>
                </div>

                <div className="validation-grid-two">
                    <div className="validation-panel">
                        <h3>Find Active Visit</h3>
                        <Form onSubmit={handleLookup}>
                            <div className="validation-form-grid">
                                <FormGroup>
                                    <Label for="redeemTicketNum">Ticket Number</Label>
                                    <div className="validation-inline-input">
                                        <Input
                                            id="redeemTicketNum"
                                            value={ticketNum}
                                            inputMode="numeric"
                                            placeholder="12345"
                                            onChange={(evt) => setTicketNum(String(evt.target.value || "").replace(/\D/g, ""))}
                                        />
                                        <Button
                                            color="secondary"
                                            type="button"
                                            onClick={() => setIsTicketScannerOpen(true)}>
                                            Scan Ticket
                                        </Button>
                                    </div>
                                </FormGroup>

                                <FormGroup>
                                    <Label for="redeemMobile">Mobile</Label>
                                    <Input
                                        id="redeemMobile"
                                        value={mobile}
                                        placeholder="15551231234"
                                        onChange={(evt) => setMobile(evt.target.value)}
                                    />
                                </FormGroup>
                            </div>

                            <Button
                                color="primary"
                                type="submit"
                                disabled={isLookingUp}>
                                {isLookingUp ? "Looking up..." : "Lookup Visit"}
                            </Button>
                        </Form>

                        {lookupStatus ? (
                            <div className="validation-kpi-grid">
                                <div className="validation-kpi-card">
                                    <span className="validation-kpi-label">Ticket</span>
                                    <strong>{lookupStatus.ticketNum || lookupStatus.lookupTicketNum || "--"}</strong>
                                </div>
                                <div className="validation-kpi-card">
                                    <span className="validation-kpi-label">Amount Due</span>
                                    <strong>{formatCents(lookupStatus.amountDueCents, lookupStatus.currency)}</strong>
                                </div>
                                <div className="validation-kpi-card">
                                    <span className="validation-kpi-label">Original Due</span>
                                    <strong>{formatCents(lookupStatus.originalAmountDueCents, lookupStatus.currency)}</strong>
                                </div>
                                <div className="validation-kpi-card">
                                    <span className="validation-kpi-label">Already Validated</span>
                                    <strong>{formatCents(lookupStatus.validatedAmountCents, lookupStatus.currency)}</strong>
                                </div>
                            </div>
                        ) : null}
                    </div>

                    <div className="validation-panel">
                        <h3>Redeem Single-Use Token</h3>
                        <Form onSubmit={handleRedeem}>
                            <FormGroup>
                                <Label for="redeemTokenCode">Validation Token</Label>
                                <div className="validation-inline-input">
                                    <Input
                                        id="redeemTokenCode"
                                        value={tokenCode}
                                        placeholder="VAL-XXXXXXXX"
                                        onChange={(evt) => setTokenCode(String(evt.target.value || "").toUpperCase())}
                                    />
                                    <Button
                                        color="secondary"
                                        type="button"
                                        onClick={() => setIsTokenScannerOpen(true)}>
                                        Scan Token
                                    </Button>
                                </div>
                            </FormGroup>

                            <FormGroup>
                                <Label for="redeemNote">Operator Note (optional)</Label>
                                <Input
                                    id="redeemNote"
                                    type="textarea"
                                    value={note}
                                    onChange={(evt) => setNote(evt.target.value)}
                                    placeholder="Validated by front desk"
                                />
                            </FormGroup>

                            <Button
                                color="primary"
                                type="submit"
                                disabled={isRedeeming}>
                                {isRedeeming ? "Redeeming..." : "Redeem Validation"}
                            </Button>
                        </Form>

                        {lastRedemption ? (
                            <div className="validation-result-banner is-success">
                                <strong>Redeemed:</strong> {lastRedemption.tokenCode} covered {formatCents(lastRedemption.coverageAmountCents)}
                            </div>
                        ) : null}
                    </div>
                </div>
            </div>

            <LiveCodeScannerModal
                isOpen={isTokenScannerOpen}
                toggle={() => setIsTokenScannerOpen(false)}
                title="Scan Validation Token"
                guideText="Center validation QR label"
                parseResult={parseValidationTokenFromPayload}
                onDetected={(value) => {
                    setTokenCode(value);
                    toast.success("Validation token scanned", { autoClose: 900 });
                }}
            />

            <LiveCodeScannerModal
                isOpen={isTicketScannerOpen}
                toggle={() => setIsTicketScannerOpen(false)}
                title="Scan Ticket QR"
                guideText="Center customer ticket QR"
                parseResult={parseTicketFromPayload}
                onDetected={(value) => {
                    setTicketNum(String(value || "").replace(/\D/g, ""));
                    toast.success("Ticket scanned", { autoClose: 900 });
                }}
            />
        </section>
    );
};

export default ValidationRedeemPage;
